import { Avatar, Badge, Button, EmptyState, appointmentStatusMeta, emptyStates, formatPrice, formatTime, nextAppointmentAction, type AppointmentStatus } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppointmentForm, type EditableAppointment } from "../components/appointment-form";
import { CheckoutPanel } from "../components/checkout-panel";
import { MonthView, WeekView, startOfMonth, startOfWeek } from "../components/planning-views";
import { ExportPlanning } from "../components/planning-export";
import { SALON_ID, SALON_TIMEZONE as TZ, api } from "../lib/trpc";
import {
  dayKeyOf,
  minutesFromDayStart,
  shiftDayKey,
  todayKey,
  zonedEndOfDay,
  zonedStartOfDay,
} from "../lib/time";

/** Hauteur d'une minute, en pixels. 1,4 px ⇒ une heure fait 84 px. */
const PX_PER_MINUTE = 1.4;
/** Aimantation du glisser-déposer. Poser un RDV à 14h07 n'a aucun sens. */
const SNAP_MINUTES = 15;
/** En deçà, c'est un clic : on ouvre le détail au lieu de déplacer. */
const DRAG_THRESHOLD_PX = 4;
/** Bornes affichées par défaut, élargies si un RDV déborde. */
const DEFAULT_RANGE = { from: 8 * 60, to: 20 * 60 };

/**
 * Le planning du jour — l'écran que Sarah garde ouvert toute la journée.
 *
 * Une colonne par employée, le temps en vertical. Trois choix de lisibilité :
 *  - la couleur de la colonne vient de `staffProfiles.calendarColor`, donc
 *    chaque employée garde SA couleur partout dans le produit ;
 *  - un trait rouge marque l'instant présent — c'est ce que l'œil cherche
 *    en premier quand on revient à l'écran ;
 *  - l'action suivante est un bouton unique sur la carte, jamais un menu.
 */
export function Planning() {
  const [dateKey, setDateKey] = useState(() => todayKey(TZ));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [mode, setMode] = useState<"day" | "week" | "month">("day");
  const [creating, setCreating] = useState<Date | null>(null);
  /** Colonne cliquée : l'employée est déjà choisie, on ne la redemande pas. */
  const [creatingStaffId, setCreatingStaffId] = useState<string | undefined>(undefined);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  /** Déplacement en cours : décalage en minutes appliqué visuellement. */
  const [drag, setDrag] = useState<{ id: string; offsetMinutes: number } | null>(null);
  const dragOrigin = useRef<{ y: number; moved: boolean } | null>(null);
  const queryClient = useQueryClient();

  const { from, to } = useMemo(() => {
    if (mode === "day") {
      return { from: zonedStartOfDay(dateKey, TZ), to: zonedEndOfDay(dateKey, TZ) };
    }
    if (mode === "week") {
      const monday = startOfWeek(dateKey);
      return {
        from: zonedStartOfDay(monday, TZ),
        to: zonedEndOfDay(shiftDayKey(monday, 6), TZ),
      };
    }
    // Mois : on charge la grille complete de six semaines, sinon les jours
    // debordants apparaitraient vides alors qu'ils sont charges.
    const gridStart = startOfWeek(startOfMonth(dateKey));
    return {
      from: zonedStartOfDay(gridStart, TZ),
      to: zonedEndOfDay(shiftDayKey(gridStart, 41), TZ),
    };
  }, [dateKey, mode]);

  const day = useQuery({
    queryKey: ["planning", mode, dateKey],
    queryFn: () => api.agenda.day.query({ salonId: SALON_ID, from, to }),
  });

  const reschedule = useMutation({
    mutationFn: (input: { appointmentId: string; blockStartsAt: Date }) =>
      api.booking.reschedule.mutate(input),
    onSuccess: () => {
      setConflict(null);
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
    onError: (error) => {
      // La contrainte EXCLUDE a tranché : le RDV revient à sa place et on
      // explique pourquoi, plutôt que de laisser une carte flotter.
      setConflict(
        error instanceof Error && error.message.includes("chevauche")
          ? error.message
          : "Ce créneau chevauche un rendez-vous existant.",
      );
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const advance = useMutation({
    mutationFn: (input: { appointmentId: string; to: AppointmentStatus }) =>
      api.agenda.advance.mutate({
        appointmentId: input.appointmentId,
        to: input.to as "arrived" | "in_progress" | "completed" | "no_show",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["planning"] }),
  });

  // Le trait « maintenant » doit avancer sans recharger la page.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const staff = day.data?.staff ?? [];
  const appointments = day.data?.appointments ?? [];

  // La grille s'ajuste : une pose qui finit à 21 h ne doit pas être coupée.
  const range = useMemo(() => {
    let { from: start, to: end } = DEFAULT_RANGE;
    for (const appointment of appointments) {
      start = Math.min(start, Math.floor(minutesFromDayStart(appointment.blockStartsAt, from) / 60) * 60);
      end = Math.max(end, Math.ceil(minutesFromDayStart(appointment.blockEndsAt, from) / 60) * 60);
    }
    return { from: Math.max(0, start), to: Math.min(24 * 60, end) };
  }, [appointments, from]);

  const height = (range.to - range.from) * PX_PER_MINUTE;
  const nowOffset = minutesFromDayStart(now, from) - range.from;
  const isToday = dateKey === todayKey(TZ);

  const selected = appointments.find((appointment) => appointment.id === selectedId) ?? null;
  const editing = appointments.find((appointment) => appointment.id === editingId) ?? null;

  /** Ouvre la création sur le créneau cliqué, aimanté au quart d'heure. */
  const createAt = (member: string | undefined, clientY: number, bounds: DOMRect) => {
    const minutes = range.from + (clientY - bounds.top) / PX_PER_MINUTE;
    const snapped = Math.min(
      range.to - SNAP_MINUTES,
      Math.max(range.from, Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES),
    );
    setCreatingStaffId(member);
    setCreating(new Date(from.getTime() + snapped * 60_000));
  };

  const closeForm = () => {
    setCreating(null);
    setCreatingStaffId(undefined);
    setEditingId(null);
  };

  /** Changer de periode ferme le detail et efface un conflit devenu hors sujet. */
  const goToDay = (next: string) => {
    setDateKey(next);
    setSelectedId(null);
    setConflict(null);
  };

  /** Le pas de navigation suit le mode : un jour, une semaine, un mois. */
  const shiftPeriod = (direction: 1 | -1) => {
    if (mode === "day") return goToDay(shiftDayKey(dateKey, direction));
    if (mode === "week") return goToDay(shiftDayKey(dateKey, 7 * direction));
    const date = new Date(`${startOfMonth(dateKey)}T12:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + direction);
    goToDay(date.toISOString().slice(0, 10));
  };

  /*
   * Glisser-deposer par CAPTURE DE POINTEUR, et non par ecouteurs sur `window`.
   *
   * La version precedente posait `pointermove` et `pointerup` sur `window` a
   * l'appui, et un `useEffect` de « filet de securite » les retirait au
   * demontage. Ce filet avait pour dependances les deux fonctions ; or
   * `endDrag` dependait de `reschedule`, que `useMutation` recree A CHAQUE
   * RENDU. L'appui declenchait un rendu — il pose l'etat de glissement —, donc
   * l'identite changeait, donc React executait le nettoyage de l'effet
   * precedent : il RETIRAIT l'ecouteur `pointerup` que l'appui venait
   * d'installer.
   *
   * Consequence : personne n'ecoutait le relachement. En vue jour, cliquer un
   * rendez-vous ne faisait rien — la selection se decide justement au
   * relachement. Le defaut ne se voyait pas en test synthetique, ou l'appui et
   * le relachement tiennent dans la meme tache, avant tout rendu.
   *
   * `setPointerCapture` supprime le probleme a la racine : l'element garde les
   * evenements jusqu'au relachement, meme si le pointeur en sort, et tout se
   * declare en `props` React. Plus d'ecouteur global, plus d'identite a
   * suivre, plus de nettoyage a synchroniser.
   */
  const finirGlissement = (appointmentId: string, offsetMinutes: number, aBouge: boolean) => {
    dragOrigin.current = null;
    setDrag(null);

    // Pas de mouvement reel, ou retour au point de depart : c'etait un clic.
    if (!aBouge || offsetMinutes === 0) {
      setSelectedId(appointmentId);
      return;
    }

    const appointment = appointments.find((item) => item.id === appointmentId);
    if (!appointment) return;

    reschedule.mutate({
      appointmentId,
      blockStartsAt: new Date(appointment.blockStartsAt.getTime() + offsetMinutes * 60_000),
    });
  };

  const startDrag = (appointmentId: string, event: React.PointerEvent<HTMLElement>) => {
    // Bouton principal uniquement : un clic droit ouvre un menu systeme.
    if (event.button !== 0) return;
    event.preventDefault();
    /*
     * La capture doit etre posee sur l'element AVANT toute mise a jour d'etat :
     * un re-rendu qui remplacerait le noeud perdrait la capture, et le
     * relachement partirait ailleurs.
     */
    event.currentTarget.setPointerCapture(event.pointerId);
    dragOrigin.current = { y: event.clientY, moved: false };
    setDrag({ id: appointmentId, offsetMinutes: 0 });
  };

  const moveDrag = (event: React.PointerEvent<HTMLElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;

    const deltaPx = event.clientY - origin.y;
    // En deca du seuil, c'est la main qui tremble, pas une intention.
    if (!origin.moved && Math.abs(deltaPx) < DRAG_THRESHOLD_PX) return;
    origin.moved = true;

    // Aimantation : le RDV ne se pose que sur des quarts d'heure.
    const snapped = Math.round(deltaPx / PX_PER_MINUTE / SNAP_MINUTES) * SNAP_MINUTES;
    setDrag((current) => (current ? { ...current, offsetMinutes: snapped } : current));
  };

  const endDrag = (appointmentId: string, event: React.PointerEvent<HTMLElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    finirGlissement(appointmentId, drag?.offsetMinutes ?? 0, origin.moved);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center gap-3 px-6 py-4">
        <h1 className="text-2xl">Planning</h1>
        <div className="flex items-center gap-1">
          <NavButton label="Période précédente" onClick={() => shiftPeriod(-1)}>
            ‹
          </NavButton>
          <button
            type="button"
            onClick={() => goToDay(todayKey(TZ))}
            className="rounded-full px-3 py-1.5 text-sm font-medium text-encre-700 transition-colors hover:bg-creme-200"
          >
            Aujourd&apos;hui
          </button>
          <NavButton label="Période suivante" onClick={() => shiftPeriod(1)}>
            ›
          </NavButton>
        </div>
        <p className="font-display text-lg text-encre-600">{periodLabel(mode, dateKey, from)}</p>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-full bg-creme-200 p-0.5">
            {(
              [
                ["day", "Jour"],
                ["week", "Semaine"],
                ["month", "Mois"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setMode(key);
                  setSelectedId(null);
                }}
                aria-pressed={mode === key}
                className={[
                  "rounded-full px-3 py-1.5 text-sm transition-colors",
                  mode === key ? "bg-white font-medium text-encre-900 shadow-soft" : "text-encre-600",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>

          <RechercheReference
            onTrouve={(dayKey, appointmentId) => {
              setMode("day");
              goToDay(dayKey);
              // `goToDay` efface la selection : on la repose APRES, sinon le
              // rendez-vous trouve serait aussitot deselectionne.
              setSelectedId(appointmentId);
            }}
          />

          <ExportPlanning dateKey={dateKey} />

          <Button size="sm" onClick={() => setCreating(defaultSlotFor(dateKey))}>
            + Rendez-vous
          </Button>
        </div>
      </header>

      {conflict && (
        <p
          role="status"
          className="mx-6 mb-3 rounded-lg bg-ambre-100 px-4 py-2.5 text-sm font-medium text-ambre-700"
        >
          {conflict}
        </p>
      )}

      {day.isPending && <p className="px-6 text-encre-400">Chargement du planning…</p>}

      {/* Un ecran blanc n'est pas un etat : on dit ce qui se passe. */}
      {day.isError && (
        <EmptyState
          variant="card"
          className="mx-6"
          glyph="🌸"
          title="Planning indisponible"
          description="Impossible de joindre le salon. Verifiez la connexion, puis reessayez."
          action={
            <Button variant="secondary" onClick={() => day.refetch()}>
              Reessayer
            </Button>
          }
        />
      )}

      {day.data && staff.length === 0 && (
        <EmptyState variant="card" className="mx-6" {...emptyStates.freeDay} />
      )}

      {staff.length > 0 && mode === "week" && (
        <div className="flex min-h-0 flex-1 gap-4 px-6 pb-6">
          <WeekView
            dateKey={dateKey}
            staff={staff}
            appointments={appointments}
            onSelect={(id) => {
              setMode("day");
              const target = appointments.find((item) => item.id === id);
              if (target) setDateKey(keyOfInstant(target.blockStartsAt));
              setSelectedId(id);
            }}
            onCreate={(startsAt) => {
              // La semaine ne montre pas de colonne par employée : le
              // formulaire propose la première, à corriger si besoin.
              setCreatingStaffId(undefined);
              setCreating(startsAt);
            }}
          />
        </div>
      )}

      {staff.length > 0 && mode === "month" && (
        <div className="flex min-h-0 flex-1 gap-4 px-6 pb-6">
          <MonthView
            dateKey={dateKey}
            appointments={appointments}
            absences={day.data?.absences ?? []}
            onPickDay={(picked) => {
              setDateKey(picked);
              setMode("day");
            }}
          />
        </div>
      )}

      {staff.length > 0 && mode === "day" && (
        <div className="flex min-h-0 flex-1 gap-4 px-6 pb-6">
          <div className="min-w-0 flex-1 overflow-auto rounded-xl bg-white shadow-soft">
            {/* En-tête collant : les prénoms restent visibles au défilement. */}
            <div
              className="sticky top-0 z-10 grid border-b border-creme-200 bg-white/95 backdrop-blur"
              style={{ gridTemplateColumns: `4rem repeat(${staff.length}, minmax(11rem, 1fr))` }}
            >
              <div />
              {staff.map((member) => (
                <div key={member.id} className="flex items-center gap-2 px-3 py-3">
                  <Avatar src={member.avatarUrl} firstName={member.firstName} size="sm" />
                  <span className="truncate text-sm font-medium text-encre-800">
                    {member.displayName}
                  </span>
                  <span
                    aria-hidden
                    className="ml-auto size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: member.calendarColor }}
                  />
                </div>
              ))}
            </div>

            <div
              className="relative grid"
              style={{
                gridTemplateColumns: `4rem repeat(${staff.length}, minmax(11rem, 1fr))`,
                // Marge haute : les libelles d'heure sont centres sur leur
                // trait (`-translate-y-1/2`), le premier passait sous
                // l'en-tete collant.
                height: height + 12,
                paddingTop: 12,
              }}
            >
              {/* Colonne des heures */}
              <div className="relative border-r border-creme-200">
                {hoursIn(range).map((minute) => (
                  <span
                    key={minute}
                    className="absolute right-2 -translate-y-1/2 text-xs tabular-nums text-encre-400"
                    style={{ top: (minute - range.from) * PX_PER_MINUTE }}
                  >
                    {String(Math.floor(minute / 60)).padStart(2, "0")}:00
                  </span>
                ))}
              </div>

              {staff.map((member) => (
                <div
                  key={member.id}
                  className="relative border-r border-creme-100 last:border-r-0"
                  /*
                    Cliquer une zone libre ouvre la saisie à cette heure, pour
                    cette employée. Les cartes de rendez-vous sont des `button` :
                    on les laisse passer, sinon ouvrir un détail créerait un
                    rendez-vous par-dessus celui qu'on voulait consulter.
                  */
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("button")) return;
                    createAt(member.id, event.clientY, event.currentTarget.getBoundingClientRect());
                  }}
                >
                  {hoursIn(range).map((minute) => (
                    <div
                      key={minute}
                      className="absolute inset-x-0 border-t border-creme-100"
                      style={{ top: (minute - range.from) * PX_PER_MINUTE }}
                    />
                  ))}

                  {appointments
                    .filter((appointment) => appointment.staffId === member.id)
                    .map((appointment) => {
                      const top =
                        minutesFromDayStart(appointment.blockStartsAt, from) - range.from;
                      const duration =
                        (appointment.blockEndsAt.getTime() - appointment.blockStartsAt.getTime()) /
                        60_000;
                      const meta = appointmentStatusMeta[appointment.status];

                      const dragging = drag?.id === appointment.id;
                      const offset = dragging ? drag.offsetMinutes : 0;

                      return (
                        <button
                          key={appointment.id}
                          type="button"
                          onPointerDown={(event) => startDrag(appointment.id, event)}
                          onPointerMove={moveDrag}
                          onPointerUp={(event) => endDrag(appointment.id, event)}
                          /* Pointeur interrompu — coupure systeme, geste
                             annule : on repose la carte sans rien deplacer. */
                          onPointerCancel={() => {
                            dragOrigin.current = null;
                            setDrag(null);
                          }}
                          onKeyDown={(event) => {
                            // Accessibilité : le déplacement doit exister au
                            // clavier, pas seulement à la souris.
                            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                            event.preventDefault();
                            const step = event.key === "ArrowUp" ? -SNAP_MINUTES : SNAP_MINUTES;
                            reschedule.mutate({
                              appointmentId: appointment.id,
                              blockStartsAt: new Date(
                                appointment.blockStartsAt.getTime() + step * 60_000,
                              ),
                            });
                          }}
                          aria-current={selectedId === appointment.id}
                          aria-label={`${appointment.client?.firstName ?? appointment.guestName ?? "Cliente"} à ${formatTime(appointment.startsAt, TZ)} — flèches haut et bas pour déplacer`}
                          className={[
                            "absolute inset-x-1 overflow-hidden rounded-md px-2 py-1 text-left ease-silk",
                            "touch-none select-none",
                            dragging
                              ? "z-30 cursor-grabbing shadow-lift"
                              : "cursor-grab transition-shadow hover:shadow-lift focus-visible:shadow-lift",
                            meta.blocking ? "" : "opacity-50 line-through",
                            selectedId === appointment.id ? "ring-2 ring-blush-500" : "",
                          ].join(" ")}
                          style={{
                            top: (top + offset) * PX_PER_MINUTE,
                            height: Math.max(duration * PX_PER_MINUTE - 2, 22),
                            // Teinte de l'employée, très diluée : lisible sans
                            // écraser le texte.
                            backgroundColor: `${member.calendarColor}22`,
                            borderLeft: `3px solid ${member.calendarColor}`,
                          }}
                        >
                          <span className="block truncate text-xs font-medium tabular-nums text-encre-800">
                            {formatTime(
                              new Date(appointment.startsAt.getTime() + offset * 60_000),
                              TZ,
                            )}{" "}
                            {appointment.client?.firstName ?? appointment.guestName ?? "Cliente"}
                          </span>
                          {duration >= 45 && (
                            <span className="block truncate text-xs text-encre-500">
                              {appointment.lines.map((line) => line.nameSnapshot).join(" + ")}
                            </span>
                          )}
                          {appointment.status === "requested" && (
                            <span className="mt-0.5 inline-block rounded bg-ambre-100 px-1 text-[10px] font-medium text-ambre-700">
                              À confirmer
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
              ))}

              {/* Instant présent : le repère le plus consulté de l'écran. */}
              {isToday && nowOffset >= 0 && nowOffset <= range.to - range.from && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
                  style={{ top: nowOffset * PX_PER_MINUTE }}
                >
                  <span className="ml-14 size-2 rounded-full bg-grenat-500" />
                  <span className="h-px flex-1 bg-grenat-500/70" />
                </div>
              )}
            </div>
          </div>

          {selected && (
            <aside className="w-80 shrink-0 overflow-auto rounded-xl bg-white p-5 shadow-soft animate-fade-up">
              <Detail
                appointment={selected}
                onAdvance={(status) =>
                  advance.mutate({ appointmentId: selected.id, to: status })
                }
                onCheckout={() => setCheckoutId(selected.id)}
                onEdit={() => setEditingId(selected.id)}
                pending={advance.isPending}
                onClose={() => setSelectedId(null)}
              />
            </aside>
          )}
        </div>
      )}

      {checkoutId && (
        <CheckoutPanel appointmentId={checkoutId} onClose={() => setCheckoutId(null)} />
      )}

      {(creating || editing) && (
        <AppointmentForm
          salonId={SALON_ID}
          staff={staff}
          defaultStaffId={creatingStaffId}
          defaultStartsAt={creating ?? editing!.blockStartsAt}
          appointment={editing ? (editing as EditableAppointment) : undefined}
          onClose={closeForm}
        />
      )}
    </div>
  );
}

type DayData = NonNullable<Awaited<ReturnType<typeof api.agenda.day.query>>>;
type Appointment = DayData["appointments"][number];

function Detail({
  appointment,
  onAdvance,
  onCheckout,
  onEdit,
  pending,
  onClose,
}: {
  appointment: Appointment;
  onAdvance: (status: AppointmentStatus) => void;
  onCheckout: () => void;
  onEdit: () => void;
  pending: boolean;
  onClose: () => void;
}) {
  const meta = appointmentStatusMeta[appointment.status];
  const action = nextAppointmentAction(appointment.status);
  const profile = appointment.client?.clientProfile;
  const allergies = profile?.allergies?.filter(Boolean) ?? [];

  return (
    <>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="font-display text-xl leading-tight">
            {appointment.client?.firstName ?? appointment.guestName ?? "Cliente"}
          </p>
          <p className="text-sm tabular-nums text-encre-500">
            {formatTime(appointment.startsAt, TZ)} – {formatTime(appointment.endsAt, TZ)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer le détail"
          className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
        >
          ×
        </button>
      </div>

      <Badge tone={meta.tone} dot>
        {meta.label}
      </Badge>

      {/* Contre-indications : toujours au-dessus du reste. */}
      {allergies.length > 0 && (
        <p
          role="alert"
          className="mt-3 rounded-md bg-grenat-100 px-3 py-2 text-sm font-medium text-grenat-700"
        >
          ⚠️ Allergies : {allergies.join(", ")}
        </p>
      )}

      <ul className="mt-4 space-y-1 text-sm">
        {appointment.lines.map((line) => (
          <li key={line.id} className="flex justify-between gap-2">
            <span className="text-encre-700">{line.nameSnapshot}</span>
            <span className="tabular-nums text-encre-500">
              {formatPrice(line.priceCentsSnapshot)}
            </span>
          </li>
        ))}
      </ul>

      {appointment.clientNote && (
        <p className="mt-3 rounded-md bg-creme-100 px-3 py-2 text-sm italic text-encre-600">
          « {appointment.clientNote} »
        </p>
      )}

      {/* Annoncée à la réservation : on le sait avant que la cliente ne s'assoie. */}
      {appointment.giftCardId && (
        <p className="mt-3 rounded-md bg-blush-50 px-3 py-2 text-sm text-blush-700">
          🎁 Carte cadeau annoncée — proposée à l&apos;encaissement
        </p>
      )}

      {appointment.inspirations.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {appointment.inspirations.map((inspiration) => (
            <img
              key={inspiration.assetId}
              src={inspiration.asset.url}
              alt={inspiration.asset.altText ?? ""}
              className="aspect-square w-full rounded-md object-cover"
              style={{ backgroundColor: inspiration.asset.dominantColor ?? undefined }}
            />
          ))}
        </div>
      )}

      {/* UNE action, jamais un menu. */}
      {action && (
        <button
          type="button"
          disabled={pending}
          onClick={() => onAdvance(action.to)}
          className="mt-5 min-h-11 w-full rounded-full bg-blush-500 font-medium text-white transition-colors hover:bg-blush-600 disabled:opacity-50"
        >
          {action.label}
        </button>
      )}

      {/*
        L'encaissement vit ICI, sur la ligne de planning — pas dans un module
        « caisse » separe qu'il faudrait aller chercher.
      */}
      {(appointment.status === "in_progress" || appointment.status === "completed") && (
        <button
          type="button"
          onClick={onCheckout}
          className="mt-2 min-h-11 w-full rounded-full bg-encre-800 font-medium text-white transition-colors hover:bg-encre-900"
        >
          Encaisser
        </button>
      )}

      {/*
        Corriger passe par le même formulaire que la saisie : prestations,
        employée, heure et cliente. Un rendez-vous encaissé est figé — le
        proposer ouvrirait sur un refus du serveur.
      */}
      {appointment.status !== "completed" && (
        <button
          type="button"
          onClick={onEdit}
          className="mt-2 min-h-11 w-full rounded-full border border-creme-300 font-medium text-encre-800 transition-colors hover:bg-creme-100"
        >
          Modifier
        </button>
      )}

      {appointment.status === "confirmed" && (
        <button
          type="button"
          disabled={pending}
          onClick={() => onAdvance("no_show")}
          className="mt-2 min-h-9 w-full rounded-full text-sm text-encre-400 transition-colors hover:bg-creme-100 hover:text-grenat-700"
        >
          Marquer absente
        </button>
      )}
    </>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="size-8 rounded-full text-lg text-encre-600 transition-colors hover:bg-creme-200"
    >
      {children}
    </button>
  );
}

/** Libelle de la periode courante, selon le mode. */
function periodLabel(mode: "day" | "week" | "month", dateKey: string, from: Date): string {
  if (mode === "month") {
    const label = new Intl.DateTimeFormat("fr-FR", {
      timeZone: TZ,
      month: "long",
      year: "numeric",
    }).format(new Date(`${dateKey}T12:00:00Z`));
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  if (mode === "week") {
    const monday = startOfWeek(dateKey);
    const sunday = shiftDayKey(monday, 6);
    const format = new Intl.DateTimeFormat("fr-FR", { timeZone: TZ, day: "numeric", month: "short" });
    return `${format.format(new Date(`${monday}T12:00:00Z`))} — ${format.format(new Date(`${sunday}T12:00:00Z`))}`;
  }

  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(from);
}

/** Creneau propose par defaut : 10h du jour affiche. */
function defaultSlotFor(dateKey: string): Date {
  return new Date(zonedStartOfDay(dateKey, TZ).getTime() + 10 * 60 * 60_000);
}

function keyOfInstant(instant: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

function hoursIn(range: { from: number; to: number }): number[] {
  const hours: number[] = [];
  for (let minute = Math.ceil(range.from / 60) * 60; minute <= range.to; minute += 60) {
    hours.push(minute);
  }
  return hours;
}

/**
 * Recherche d'un rendez-vous par son numero.
 *
 * L'usage du comptoir et du telephone : la cliente donne « 1043 », on veut
 * arriver dessus sans parcourir le planning. Un champ court, un Entree, et
 * l'ecran se deplace au bon jour avec le rendez-vous ouvert.
 */
function RechercheReference({
  onTrouve,
}: {
  onTrouve: (dayKey: string, appointmentId: string) => void;
}) {
  const [saisie, setSaisie] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  const chercher = useMutation({
    mutationFn: (reference: number) =>
      api.agenda.findByReference.query({ salonId: SALON_ID, reference }),
    onSuccess: (rdv) => {
      setErreur(null);
      setSaisie("");
      onTrouve(dayKeyOf(rdv.startsAt, TZ), rdv.id);
    },
    onError: (error) => setErreur(error.message),
  });

  return (
    <div className="relative">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // On accepte « n° 1043 » et « #1043 » : on ne retient que les
          // chiffres, personne ne recopie un numero au caractere pres.
          const reference = Number(saisie.replace(/\D/g, ""));
          if (reference > 0) chercher.mutate(reference);
        }}
      >
        <input
          value={saisie}
          onChange={(event) => {
            setSaisie(event.target.value);
            setErreur(null);
          }}
          placeholder="N° réservation"
          inputMode="numeric"
          aria-label="Rechercher un rendez-vous par son numéro"
          className="h-9 w-36 rounded-full border border-creme-300 bg-white px-4 text-sm outline-none placeholder:text-encre-400 focus:border-blush-400"
        />
      </form>

      {erreur && (
        <p
          role="alert"
          className="absolute right-0 top-10 z-10 w-56 rounded-lg bg-grenat-100 px-3 py-2 text-xs text-grenat-700 shadow-soft"
        >
          {erreur}
        </p>
      )}
    </div>
  );
}
